// src/pages/monthly/ApplyFormComponent2.tsx

import React, { useState, useMemo, useCallback } from 'react';
import { RoomTypeCard } from '@/components/monthly/RoomTypeCard';
import { CostSummary } from '@/components/monthly/CostSummary';
import { AgreementComponent } from './AgreementComponent';
import { useParams, useLocation } from 'react-router-dom';
import { InvoiceComponent } from './ApplyForms/InvoiceComponent';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import {
  getSanctumCsrfCookie,
  getXsrfToken,
  postContractSubmission,
} from '@/API/apiService';

/** 見積もりフォームの型 */
interface EstimateFormData {
  fromDate: string;
  toDate: string;
  guests: string;
}

/** 契約者情報フォームの型 */
interface ContractInfoFormData {
  firstName: string;
  lastName: string;
  firstNameKana: string;
  lastNameKana: string;
  country: string;
  address: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  gender: string;
  email: string;
  phoneNumber: string;
}

/** 勤務先情報フォームの型 */
interface WorkInfoFormData {
  companyName: string;
  department?: string;
  companyAddress?: string;
  companyPhone?: string;
  industry?: string;
  jobType?: string;
  position?: string;
  annualIncome?: string;
}

/** 入居者情報フォームの型 */
interface ResidentInfoFormData {
  firstName: string;
  lastName: string;
  firstNameKana: string;
  lastNameKana: string;
  relation: string;
  country: string;
  address: string;
  birthYear: string;
  birthMonth: string;
  birthDay: string;
  ResidentGender?: string;
  email: string;
  phoneNumber: string;
}

/** 緊急連絡先フォームの型 */
interface EmergencyContactFormData {
  firstName: string;
  lastName: string;
  firstNameKana?: string;
  lastNameKana?: string;
  relation?: string;
  address?: string;
  birthYear?: string;
  birthMonth?: string;
  birthDay?: string;
  EmergencyGender?: string;
  phoneNumber?: string;
}

/** 身分証明書アップロードフォームの型 */
interface IDUploadFormData {
  idType: string;
  frontFile: File | null;
  backFile: File | null;
}

/** Step1 で入力・アップロードされた全データ構造 */
export interface FormDataStructure {
  estimateData?: EstimateFormData;
  contractData?: ContractInfoFormData;
  workData?: WorkInfoFormData | null;
  residentData?: ResidentInfoFormData | null;
  emergencyData?: EmergencyContactFormData | null;
  idUploadData?: IDUploadFormData | null;
  // RoomTypeCard 表示用項目
  roomType?: string;
  roomType_en?: string;
  imageUrl?: string;
  capacity?: string;
  area?: string;
  area_en?: string;
  description?: string;
  description_en?: string;
  propertyName?: string;
  propertyName_en?: string;
  price?: number;
}

/** Step2 コンポーネントの props */
interface ApplyFormComponent2Props {
  formData: FormDataStructure;
  onNext: () => void;
  onBack: () => void;
}

/** AgreementComponent から受け取るデータ型 */
interface AgreementData {
  signature: string;
  date: string;
  isAgreed: boolean;
}

/** 100円未満を切り捨てる関数 */
const truncateToHundreds = (value: number) => Math.floor(value / 100) * 100;

/** 日付フォーマット "YYYY-MM-DD" に整形する関数 */
const formatDate = (year: string, month: string, day: string): string => {
  const m = month.padStart(2, '0');
  const d = day.padStart(2, '0');
  return `${year}-${m}-${d}`;
};

export const ApplyFormComponent2: React.FC<ApplyFormComponent2Props> = ({
  formData,
  onNext,
  onBack,
}) => {
  const { property_id, room_id } = useParams();
  const location = useLocation();
  const langSegment = location.pathname.split('/')[1];
  const useEnglish = langSegment === 'en';
  const lang = useEnglish ? 'en' : 'ja';

  const contractHeading = useEnglish ? 'Contract Info' : '契約者情報';
  const workHeading = useEnglish ? 'Work Info' : 'ご勤務先情報';
  const residentHeading = useEnglish ? 'Resident Info' : 'ご入居される方の情報';
  const emergencyHeading = useEnglish ? 'Emergency Contact' : '緊急連絡先';
  const idUploadHeading = useEnglish ? 'ID Upload' : '身分証明書のアップロード';

  const data = formData;
  const price = data.price ?? 0;
  const initialFee = truncateToHundreds(price * 0.1);
  const cleaningFee = truncateToHundreds(price * 0.2);
  const totalCost = price + initialFee + cleaningFee;

  // AgreementComponent の onChange ハンドラ
  const [agreementData, setAgreementData] = useState<AgreementData>({
    signature: '',
    date: '',
    isAgreed: false,
  });

  const formatNumber = (value?: string | number): string => {
    if (value == null || value === '') return '';
    const num = typeof value === 'string' ? Number(value) : value;
    return isNaN(num) ? '' : num.toLocaleString();
  };

  const handleAgreementChange = useCallback((newData: AgreementData) => {
    setAgreementData((prev) => {
      if (
        prev.signature === newData.signature &&
        prev.date === newData.date &&
        prev.isAgreed === newData.isAgreed
      ) {
        return prev;
      }
      return newData;
    });
  }, []);

  // 同意と署名が揃っているかどうかを判定
  const isAgreementValid = useMemo(() => {
    return (
      agreementData.isAgreed &&
      agreementData.signature.trim().length > 0 &&
      agreementData.date.trim().length > 0
    );
  }, [agreementData]);

  const [isLoading, setIsLoading] = useState(false);

  const handleEnterClick = async () => {
    setIsLoading(true);
    try {
      await getSanctumCsrfCookie();
      const xsrfToken = getXsrfToken();

      const applicantBirthDay =
        data.contractData &&
        data.contractData.birthYear &&
        data.contractData.birthMonth &&
        data.contractData.birthDay
          ? formatDate(
              data.contractData.birthYear,
              data.contractData.birthMonth,
              data.contractData.birthDay
            )
          : '';
      const occupantBirthDay =
        data.residentData &&
        data.residentData.birthYear &&
        data.residentData.birthMonth &&
        data.residentData.birthDay
          ? formatDate(
              data.residentData.birthYear,
              data.residentData.birthMonth,
              data.residentData.birthDay
            )
          : '';
      const emergencyBirthDay =
        data.emergencyData &&
        data.emergencyData.birthYear &&
        data.emergencyData.birthMonth &&
        data.emergencyData.birthDay
          ? formatDate(
              data.emergencyData.birthYear,
              data.emergencyData.birthMonth,
              data.emergencyData.birthDay
            )
          : '';

      const applicantPayload = {
        first_name: data.contractData?.firstName || '',
        last_name: data.contractData?.lastName || '',
        first_name_roman_alphabet: data.contractData?.firstNameKana || '',
        last_name_roman_alphabet: data.contractData?.lastNameKana || '',
        country: data.contractData?.country || '',
        country_name: data.contractData?.country || '',
        address: data.contractData?.address || '',
        email: data.contractData?.email || '',
        birth_day: applicantBirthDay,
        sex: data.contractData?.gender || '',
        phone_number: data.contractData?.phoneNumber || '',
        workplace: data.workData?.companyName || '',
        affiliation: data.workData?.department || '',
        businessAddress: data.workData?.companyAddress || '',
        businessPhoneNumber: data.workData?.companyPhone || '',
        industry: data.workData?.industry || '',
        occupation: data.workData?.jobType || '',
        post: data.workData?.position || '',
        annual_income: data.workData?.annualIncome
          ? parseFloat(data.workData.annualIncome)
          : 0,
        country_id: 'JP',
      };
      const contractPayload = {
        property_name: data.propertyName || '',
        room_type_name: data.roomType || '',
        room_area: data.area ? parseFloat(data.area) : 0,
        starting_total: totalCost,
        start_date: data.estimateData?.fromDate || '',
        end_date: data.estimateData?.toDate || '',
        language: lang,
        number_of_users: data.estimateData?.guests
          ? parseInt(data.estimateData.guests)
          : 1,
        prices: {
          monthly_cost: { monthly_price: price },
          starting_cost: { cleaning_fee: cleaningFee, service_fee: initialFee },
        },
        room_type_id: room_id,
        site_controller_id: 1,
        signature: agreementData.signature,
        signature_date: agreementData.date,
      };

      const occupantPayload = {
        first_name: data.residentData?.firstName || '',
        last_name: data.residentData?.lastName || '',
        first_name_roman_alphabet: data.residentData?.firstNameKana || '',
        last_name_roman_alphabet: data.residentData?.lastNameKana || '',
        relationship: data.residentData?.relation || '',
        country: data.residentData?.country || '',
        address: data.residentData?.address || '',
        birth_day: occupantBirthDay,
        sex: data.residentData?.ResidentGender || '',
        email: data.residentData?.email || '',
        phone_number: data.residentData?.phoneNumber || '',
      };

      const emergencyPayload = {
        first_name: data.emergencyData?.firstName || '',
        last_name: data.emergencyData?.lastName || '',
        first_name_roman_alphabet: data.emergencyData?.firstNameKana || '',
        last_name_roman_alphabet: data.emergencyData?.lastNameKana || '',
        relationship: data.emergencyData?.relation || '',
        address: data.emergencyData?.address || '',
        birth_day: emergencyBirthDay,
        sex: data.emergencyData?.EmergencyGender || '',
        phone_number: data.emergencyData?.phoneNumber || '',
      };

      const formDataToSend = new FormData();

      // applicant 用フィールド
      formDataToSend.append(
        'applicant[first_name]',
        applicantPayload.first_name
      );
      formDataToSend.append('applicant[last_name]', applicantPayload.last_name);
      formDataToSend.append(
        'applicant[first_name_roman_alphabet]',
        applicantPayload.first_name_roman_alphabet
      );
      formDataToSend.append(
        'applicant[last_name_roman_alphabet]',
        applicantPayload.last_name_roman_alphabet
      );
      formDataToSend.append('applicant[country]', applicantPayload.country);
      formDataToSend.append(
        'applicant[country_name]',
        applicantPayload.country_name
      );
      formDataToSend.append('applicant[address]', applicantPayload.address);
      formDataToSend.append('applicant[email]', applicantPayload.email);
      formDataToSend.append('applicant[birth_day]', applicantPayload.birth_day);
      formDataToSend.append('applicant[sex]', applicantPayload.sex);
      formDataToSend.append(
        'applicant[phone_number]',
        applicantPayload.phone_number
      );
      formDataToSend.append('applicant[workplace]', applicantPayload.workplace);
      formDataToSend.append(
        'applicant[affiliation]',
        applicantPayload.affiliation
      );
      formDataToSend.append(
        'applicant[business_address]',
        applicantPayload.businessAddress
      );
      formDataToSend.append(
        'applicant[business_phone_number]',
        applicantPayload.businessPhoneNumber
      );
      formDataToSend.append('applicant[industry]', applicantPayload.industry);
      formDataToSend.append(
        'applicant[occupation]',
        applicantPayload.occupation
      );
      formDataToSend.append('applicant[post]', applicantPayload.post);
      formDataToSend.append(
        'applicant[annual_income]',
        applicantPayload.annual_income.toString()
      );

      // 身分証明書
      if (data.idUploadData?.frontFile) {
        formDataToSend.append(
          'applicant[image][0]',
          data.idUploadData.frontFile
        );
      }
      if (data.idUploadData?.backFile) {
        formDataToSend.append(
          'applicant[image][1]',
          data.idUploadData.backFile
        );
      }

      // contract 用フィールド
      formDataToSend.append(
        'contract[property_name]',
        contractPayload.property_name
      );
      formDataToSend.append(
        'contract[room_type_name]',
        contractPayload.room_type_name
      );
      formDataToSend.append(
        'contract[room_area]',
        contractPayload.room_area.toString()
      );
      formDataToSend.append(
        'contract[starting_total]',
        contractPayload.starting_total.toString()
      );
      formDataToSend.append('contract[start_date]', contractPayload.start_date);
      formDataToSend.append('contract[end_date]', contractPayload.end_date);
      formDataToSend.append('contract[language]', contractPayload.language);
      formDataToSend.append(
        'contract[number_of_users]',
        contractPayload.number_of_users.toString()
      );
      // 月額費用
      formDataToSend.append(
        'contract[prices][monthly_cost][monthly_price]',
        contractPayload.prices.monthly_cost.monthly_price.toString()
      );
      // 初期費用
      formDataToSend.append(
        'contract[prices][starting_cost][cleaning_fee]',
        contractPayload.prices.starting_cost.cleaning_fee.toString()
      );
      formDataToSend.append(
        'contract[prices][starting_cost][service_fee]',
        contractPayload.prices.starting_cost.service_fee.toString()
      );
      formDataToSend.append(
        'contract[room_type_id]',
        contractPayload.room_type_id?.toString() || ''
      );
      formDataToSend.append(
        'contract[site_controller_id]',
        contractPayload.site_controller_id.toString()
      );

      // ** 署名・署名日を contract に追加 (重要) **
      formDataToSend.append(
        'contract[signature]',
        contractPayload.signature ?? ''
      );
      formDataToSend.append(
        'contract[signature_date]',
        contractPayload.signature_date ?? ''
      );

      // occupant 用フィールド
      formDataToSend.append('occupant[first_name]', occupantPayload.first_name);
      formDataToSend.append('occupant[last_name]', occupantPayload.last_name);
      formDataToSend.append(
        'occupant[first_name_roman_alphabet]',
        occupantPayload.first_name_roman_alphabet
      );
      formDataToSend.append(
        'occupant[last_name_roman_alphabet]',
        occupantPayload.last_name_roman_alphabet
      );
      formDataToSend.append(
        'occupant[relationship]',
        occupantPayload.relationship
      );
      formDataToSend.append('occupant[country]', occupantPayload.country);
      formDataToSend.append('occupant[address]', occupantPayload.address);
      formDataToSend.append('occupant[birth_day]', occupantPayload.birth_day);
      formDataToSend.append('occupant[sex]', occupantPayload.sex);
      formDataToSend.append('occupant[email]', occupantPayload.email);
      formDataToSend.append(
        'occupant[phone_number]',
        occupantPayload.phone_number
      );

      // emergency_contact 用フィールド
      formDataToSend.append(
        'emergency_contact[first_name]',
        emergencyPayload.first_name
      );
      formDataToSend.append(
        'emergency_contact[last_name]',
        emergencyPayload.last_name
      );
      formDataToSend.append(
        'emergency_contact[first_name_roman_alphabet]',
        emergencyPayload.first_name_roman_alphabet
      );
      formDataToSend.append(
        'emergency_contact[last_name_roman_alphabet]',
        emergencyPayload.last_name_roman_alphabet
      );
      formDataToSend.append(
        'emergency_contact[relationship]',
        emergencyPayload.relationship
      );
      formDataToSend.append(
        'emergency_contact[address]',
        emergencyPayload.address
      );
      formDataToSend.append(
        'emergency_contact[birth_day]',
        emergencyPayload.birth_day
      );
      formDataToSend.append('emergency_contact[sex]', emergencyPayload.sex);
      formDataToSend.append(
        'emergency_contact[phone_number]',
        emergencyPayload.phone_number
      );

      // その他の基本情報
      formDataToSend.append('property_id', property_id || '');
      formDataToSend.append('room_id', room_id || '');
      formDataToSend.append('price', price.toString());
      formDataToSend.append('initialFee', initialFee.toString());
      formDataToSend.append('cleaningFee', cleaningFee.toString());
      formDataToSend.append('totalCost', totalCost.toString());
      formDataToSend.append('lang', lang);

      const response = await postContractSubmission(formDataToSend);

      // バリデーションエラーなど
      if (response.status === 422) {
        localStorage.setItem(
          'reservationError',
          lang === 'ja'
            ? '指定された期間では空室がありません。違う期間でお試しください。'
            : 'No vacancy for the selected period. Please try a different range.'
        );
        onBack();
        return;
      }
      if (response.status !== 200) {
        localStorage.setItem(
          'reservationError',
          lang === 'ja'
            ? '予期せぬエラーが発生しました。時間をおいてお試しください。'
            : 'An unexpected error occurred. Please try again later.'
        );
        onBack();
        return;
      }

      onNext();
    } catch (error) {
      console.error('Error submitting contract:', error);
      localStorage.setItem(
        'reservationError',
        lang === 'ja'
          ? '予期せぬエラーが発生しました。時間をおいてお試しください。'
          : 'An unexpected error occurred. Please try again later.'
      );
      onBack();
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center bg-whiteYellow">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="bg-whiteYellow">
      <div className="max-w-[600px] max-md:max-w-md mx-auto px-4 rounded-md pb-32">
        {/* ヘッダー */}
        <div className="rounded-md mb-6 pt-20">
          <h3 className="text-xl mb-2">
            {lang === 'ja' ? (
              <>
                <span>ご予約はまだ完了していません。</span>
              </>
            ) : (
              <>
                <span>Your reservation</span>
                <br />
                <span>is not complete yet.</span>
              </>
            )}
          </h3>
          <p className="text-[11px]">
            {lang === 'ja'
              ? 'ご登録情報をご確認の上、署名および個人情報の取り扱いの同意をお願いします。'
              : 'Please confirm your registration details and register your credit card details.'}
          </p>
          <div className="mt-7">
            <RoomTypeCard
              propertyName={data.propertyName || ''}
              propertyName_en={data.propertyName_en}
              roomType={data.roomType || ''}
              roomType_en={data.roomType_en}
              imageUrl={data.imageUrl || ''}
              capacity={data.capacity || ''}
              area={`${data.area}m²`}
              area_en={data.area_en}
              description={data.description || ''}
              description_en={data.description_en}
              useEnglish={useEnglish}
            />
          </div>
        </div>

        {data.estimateData && (
          <div className="my-4">
            <p className="text-sm">
              {lang === 'ja' ? '人数:' : 'Guests:'} {data.estimateData.guests}
            </p>
            <p className="text-sm">
              {lang === 'ja' ? '期間:' : 'Period:'} {data.estimateData.fromDate}
              ～ {data.estimateData.toDate}
            </p>
          </div>
        )}

        {/* コストサマリー */}
        <div className="mb-5 md:flex md:space-x-4">
          <div className="mb-5 md:mb-0 md:flex-1">
            <CostSummary
              title={lang === 'ja' ? '月額費用' : 'Monthly Cost'}
              subtotal={price}
              insurances={[]}
            />
          </div>
          <div className="md:flex-1">
            <CostSummary
              title={lang === 'ja' ? '初期費用' : 'Initial Cost'}
              subtotal={0}
              insurances={[initialFee, cleaningFee]}
            />
          </div>
        </div>

        {/* 契約者情報 */}
        <div className="mt-8 pt-4">
          <div className="flex items-center">
            <span className="w-4 h-4 bg-black mr-2" />
            <h4 className="font-bold text-lg">{contractHeading}</h4>
          </div>
          <hr className="border-t-2 border-black mb-5" />
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '名:' : 'First Name:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.firstName || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '姓:' : 'Last Name:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.lastName || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '名（カナ）:' : 'First Name Kana:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.firstNameKana || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '姓（カナ）:' : 'Last Name Kana:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.lastNameKana || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '国籍:' : 'Country:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.country || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '住所:' : 'Address:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.address || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '生年月日:' : 'Birthday:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.birthYear}-{data.contractData?.birthMonth}-
              {data.contractData?.birthDay}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '性別:' : 'Gender:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.gender || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? 'メール:' : 'Email:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.email || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '電話番号:' : 'Phone:'}
            </span>
            <span className="text-sm font-bold">
              {data.contractData?.phoneNumber || ''}
            </span>
          </p>
        </div>

        {/* 勤務先情報 */}
        <div className="mt-8 pt-4">
          <div className="flex items-center">
            <span className="w-4 h-4 bg-black mr-2" />
            <h4 className="font-bold text-lg">{workHeading}</h4>
          </div>
          <hr className="border-t-2 border-black mb-5" />
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '会社名:' : 'Company Name:'}
            </span>
            <span className="text-sm font-bold">
              {data.workData?.companyName || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '部署:' : 'Department:'}
            </span>
            <span className="text-sm font-bold">
              {data.workData?.department || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '会社住所:' : 'Company Address:'}
            </span>
            <span className="text-sm font-bold">
              {data.workData?.companyAddress || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '会社電話番号:' : 'Company Phone:'}
            </span>
            <span className="text-sm font-bold">
              {data.workData?.companyPhone || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '業種:' : 'Industry:'}
            </span>
            <span className="text-sm font-bold">
              {data.workData?.industry || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '職種:' : 'Job Type:'}
            </span>
            <span className="text-sm font-bold">
              {data.workData?.jobType || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '役職:' : 'Position:'}
            </span>
            <span className="text-sm font-bold">
              {data.workData?.position || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '年収:' : 'Annual Income:'}
            </span>
            <span className="text-sm font-bold">
              {formatNumber(data.workData?.annualIncome)}
            </span>
          </p>
        </div>

        {/* ご入居される方の情報 */}
        <div className="mt-8 pt-4">
          <div className="flex items-center">
            <span className="w-4 h-4 bg-black mr-2" />
            <h4 className="font-bold text-lg">{residentHeading}</h4>
          </div>
          <hr className="border-t-2 border-black mb-5" />
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '名:' : 'First Name:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.firstName || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '姓:' : 'Last Name:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.lastName || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '名（カナ）:' : 'First Name Kana:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.firstNameKana || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '姓（カナ）:' : 'Last Name Kana:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.lastNameKana || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '関係:' : 'Relation:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.relation || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '国籍:' : 'Country:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.country || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '住所:' : 'Address:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.address || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '生年月日:' : 'Birthday:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.birthYear}-{data.residentData?.birthMonth}-
              {data.residentData?.birthDay}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '性別:' : 'Gender:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.ResidentGender || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? 'メール:' : 'Email:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.email || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '電話番号:' : 'Phone Number:'}
            </span>
            <span className="text-sm font-bold">
              {data.residentData?.phoneNumber || ''}
            </span>
          </p>
        </div>

        {/* 緊急連絡先 */}
        <div className="mt-8 pt-4">
          <div className="flex items-center">
            <span className="w-4 h-4 bg-black mr-2" />
            <h4 className="font-bold text-lg">{emergencyHeading}</h4>
          </div>
          <hr className="border-t-2 border-black mb-5" />
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '名:' : 'First Name:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.firstName || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '姓:' : 'Last Name:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.lastName || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '名（カナ）:' : 'First Name Kana:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.firstNameKana || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '姓（カナ）:' : 'Last Name Kana:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.lastNameKana || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '関係:' : 'Relation:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.relation || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '住所:' : 'Address:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.address || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '生年月日:' : 'Birthday:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.birthYear}-{data.emergencyData?.birthMonth}-
              {data.emergencyData?.birthDay}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '性別:' : 'Gender:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.EmergencyGender || ''}
            </span>
          </p>
          <p className="flex mb-2 mx-5 items-center">
            <span className="mr-6 w-20 text-xs">
              {lang === 'ja' ? '電話番号:' : 'Phone Number:'}
            </span>
            <span className="text-sm font-bold">
              {data.emergencyData?.phoneNumber || ''}
            </span>
          </p>
        </div>

        {/* 身分証明書のアップロード */}
        <div className="mt-8 pt-4">
          <div className="flex items-center">
            <span className="w-4 h-4 bg-black mr-2" />
            <h4 className="font-bold text-lg">{idUploadHeading}</h4>
          </div>
          <hr className="border-t-2 border-black mb-5" />
          {data.idUploadData ? (
            <>
              <p className="flex mb-2 mx-5 items-center">
                <span className="mr-6 w-20 text-xs">
                  {lang === 'ja' ? 'IDタイプ:' : 'ID Type:'}
                </span>
                <span className="text-sm font-bold">
                  {data.idUploadData.idType}
                </span>
              </p>
              {data.idUploadData.frontFile && data.idUploadData.frontFile && (
                <div className="mb-2 mx-5">
                  <div className="text-gray-700 text-sm">
                    {lang === 'ja' ? '表面:' : 'Front:'}
                  </div>
                  <img
                    src={URL.createObjectURL(data.idUploadData.frontFile)}
                    alt="Front ID"
                    className="w-40 h-auto border border-gray-300"
                  />
                </div>
              )}
              {data.idUploadData.backFile &&
                data.idUploadData.backFile instanceof File && (
                  <div className="mb-2 mx-5">
                    <div className="text-gray-700 text-sm">
                      {lang === 'ja' ? '裏面:' : 'Back:'}
                    </div>
                    <img
                      src={URL.createObjectURL(data.idUploadData.backFile)}
                      alt="Back ID"
                      className="w-40 h-auto border border-gray-300"
                    />
                  </div>
                )}
            </>
          ) : (
            <p className="text-sm text-gray-600 mx-5">
              {lang === 'ja'
                ? '身分証明書のアップロードデータがありません。'
                : 'No ID Upload Data available.'}
            </p>
          )}
        </div>

        {/* Invoice コンポーネント */}
        <div className="mt-8">
          {data.estimateData && (
            <InvoiceComponent estimateData={data.estimateData} />
          )}
        </div>

        {/* Agreement / 署名・同意 */}
        <div className="rounded-md">
          <AgreementComponent
            onChange={(newData) => handleAgreementChange(newData)}
          />
          <div className="flex justify-center space-x-[0.625rem]">
            <button
              onClick={onBack}
              className="bg-gray-400 text-white py-2 px-4 focus:outline-none w-[120px]"
            >
              {lang === 'ja' ? '戻る' : 'Go Back'}
            </button>
            <button
              onClick={handleEnterClick}
              disabled={!isAgreementValid}
              className={`${
                isAgreementValid ? 'bg-black' : 'bg-gray-300'
              } text-white py-2 px-4 focus:outline-none w-[120px]`}
            >
              {lang === 'ja' ? '確定' : 'Complete'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplyFormComponent2;
